import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Snowflake, Wrench, Shield, Zap, MapPin, Calendar, ChevronRight, Thermometer, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { ReviewsSection } from "@/components/common/ReviewsSection";
import { InstagramCTA } from "@/components/common/InstagramCTA";
import { InstagramFeed } from "@/components/common/InstagramFeed";

const steps = [
  { icon: Calendar, title: "Agende", text: "Escolha data, horário e local do atendimento em segundos." },
  { icon: MapPin, title: "Vamos até você", text: "Nosso técnico vai onde seu carro estiver." },
  { icon: Wrench, title: "Diagnóstico", text: "Avaliação completa e orçamento transparente antes de aprovar." },
  { icon: CheckCircle2, title: "Serviço concluído", text: "Pagamento no local via PIX, cartão ou dinheiro." },
];

export default function Landing() {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [fees, setFees] = useState({ travel_fee: 0, diagnosis_fee: 0 });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
    api.get("/config/fees").then((r) => setFees(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40"></div>
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "radial-gradient(1200px 500px at 20% 20%, rgba(14,165,233,0.25), transparent), radial-gradient(1000px 400px at 80% 80%, rgba(6,182,212,0.2), transparent)",
          }}
        ></div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-16 pb-24 sm:pt-24 sm:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-up">
              <Badge className="mb-6 bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/20 font-mono text-xs uppercase tracking-widest" data-testid="hero-badge">
                <Snowflake className="w-3 h-3 mr-2" /> Atendimento delivery
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
                Ar-condicionado automotivo
                <span className="block bg-gradient-to-r from-sky-400 via-cyan-300 to-sky-500 bg-clip-text text-transparent">
                  onde seu carro estiver.
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
                Diagnóstico, recarga de gás, troca de compressor e higienização. Técnico qualificado, equipamento profissional, orçamento transparente — tudo sem sair da sua casa ou escritório.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={() => nav("/agendar")}
                  className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold btn-shine h-12 px-8"
                  data-testid="hero-cta-schedule"
                >
                  <Calendar className="w-4 h-4 mr-2" /> Agendar atendimento
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => nav("/servicos")}
                  className="border-sky-500/30 text-sky-300 hover:bg-sky-500/10 hover:text-sky-200 h-12 px-8"
                  data-testid="hero-cta-services"
                >
                  Ver serviços <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                <div>
                  <div className="text-2xl font-black text-sky-400 font-mono">24h</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Resposta</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-cyan-400 font-mono">6m</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Garantia</div>
                </div>
                <div>
                  <div className="text-2xl font-black text-sky-300 font-mono">500+</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Atendimentos</div>
                </div>
              </div>
            </div>

            <div className="relative fade-up" style={{ animationDelay: "150ms" }}>
              <div className="relative rounded-2xl overflow-hidden border border-sky-500/20 glow-cyan">
                <img
                  src="https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                  alt="Técnico refrigerista"
                  className="w-full h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-slate-900/80 backdrop-blur-md border border-sky-500/30 rounded-xl p-4 flex items-center gap-4">
                    <Thermometer className="w-10 h-10 text-sky-400 shrink-0" />
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-sky-400">Taxa de deslocamento</div>
                      <div className="text-2xl font-black text-white">R$ {Number(fees.travel_fee).toFixed(2)}</div>
                      <div className="text-xs text-slate-400">+ orçamento transparente antes do serviço</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-cyan-400 text-slate-950 px-4 py-2 rounded-full font-mono text-xs font-bold uppercase tracking-widest rotate-3 shadow-xl">
                Novo
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 border-t border-sky-500/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-3">Processo simples</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Como funciona o atendimento delivery</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <Card
                key={i}
                className="bg-slate-900/50 border-slate-800 hover:border-sky-500/40 transition-all hover:-translate-y-1"
                data-testid={`how-step-${i}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-sky-500/15 text-sky-400 flex items-center justify-center">
                      <s.icon className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-mono text-sky-400">0{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FEE TRANSPARENCY */}
      <section className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <Card className="bg-gradient-to-br from-slate-900 to-slate-900/50 border-sky-500/20 overflow-hidden">
            <CardContent className="p-8 sm:p-12 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-3">Transparência total</div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-4">Taxas visíveis antes de confirmar</h3>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
                    <span><strong className="text-white">Taxa de deslocamento R$ {Number(fees.travel_fee).toFixed(2)}</strong> — somada ao valor do serviço aprovado.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
                    <span><strong className="text-white">Taxa de diagnóstico R$ {Number(fees.diagnosis_fee).toFixed(2)}</strong> — cobrada apenas se o orçamento não for aprovado.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
                    <span><strong className="text-white">Pagamento no local</strong> — PIX, cartão ou dinheiro, depois do serviço concluído.</span>
                  </li>
                </ul>
              </div>
              <div className="rounded-xl bg-slate-950/60 border border-sky-500/20 p-6 font-mono text-sm">
                <div className="text-xs uppercase tracking-widest text-sky-400 mb-4">Exemplo de orçamento</div>
                <div className="space-y-3 text-slate-300">
                  <div className="flex justify-between"><span>Recarga de gás R134a</span><span className="text-white">R$ 250,00</span></div>
                  <div className="flex justify-between"><span>Taxa de deslocamento</span><span className="text-white">R$ {Number(fees.travel_fee).toFixed(2)}</span></div>
                  <div className="h-px bg-slate-800"></div>
                  <div className="flex justify-between text-base font-bold"><span className="text-white">Total</span><span className="text-sky-400">R$ {(250 + Number(fees.travel_fee)).toFixed(2)}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 border-t border-sky-500/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-3">Catálogo</div>
              <h2 className="text-3xl sm:text-4xl font-bold">Nossos serviços</h2>
            </div>
            <Button variant="ghost" onClick={() => nav("/servicos")} className="text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 w-fit" data-testid="landing-view-all-services">
              Ver todos <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Card key={s.service_id} className="bg-slate-900/50 border-slate-800 overflow-hidden group hover:border-sky-500/40 transition-all" data-testid={`service-card-${s.service_id}`}>
                {s.image_url && (
                  <div className="h-40 overflow-hidden">
                    <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white mb-2">{s.name}</h3>
                  <p className="text-sm text-slate-400 mb-4 line-clamp-2">{s.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-black text-sky-400 font-mono">R$ {Number(s.price).toFixed(2)}</span>
                    <span className="text-xs text-slate-500 font-mono">~{s.duration_minutes}min</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <ReviewsSection />
      <InstagramFeed />
      <InstagramCTA />
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-6">
            Pronto para o <span className="text-sky-400">frio</span> de volta?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Agende agora e tenha o ar-condicionado do seu carro em perfeito funcionamento sem sair de onde você está.
          </p>
          <Button
            size="lg"
            onClick={() => nav("/agendar")}
            className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold btn-shine h-14 px-10 text-base"
            data-testid="landing-bottom-cta"
          >
            Agendar meu atendimento <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
